/**
 * Shared motion state - tracks active motion (planet drag or time motion) across components
 * Used as backup for late-mounting components (primary is event-driven query/response)
 */

type MotionType = 'planet-drag' | 'time-motion';

interface ActiveMotionState {
  type: MotionType;
  chartId: string;
  planet?: string; // Only for planet-drag
}

let activeMotionState: ActiveMotionState | null = null;

/**
 * Set active planet drag (called when drag starts)
 */
export const setActivePlanetDrag = (chartId: string, planet: string): void => {
  activeMotionState = { type: 'planet-drag', chartId, planet };
};

/**
 * Set active time motion (called when time motion starts)
 */
export const setActiveTimeMotion = (chartId: string): void => {
  activeMotionState = { type: 'time-motion', chartId };
};

/**
 * Clear active motion (called when any motion ends)
 */
export const clearActiveMotion = (chartId: string): void => {
  if (activeMotionState?.chartId === chartId) {
    activeMotionState = null;
  }
};

/**
 * Get the currently active motion for a chart
 * Returns null if no motion is active
 */
export const getActiveMotion = (chartId: string): ActiveMotionState | null => {
  if (activeMotionState?.chartId === chartId) {
    return activeMotionState;
  }
  return null;
};

// =============================================================================
// Legacy API (backward compatibility)
// =============================================================================

/**
 * @deprecated Use setActivePlanetDrag instead
 */
export const setActiveDragPlanet = setActivePlanetDrag;

/**
 * @deprecated Use clearActiveMotion instead
 */
export const clearActiveDragPlanet = clearActiveMotion;

/**
 * Get the currently active drag planet for a chart
 * Returns null if no planet drag is active
 */
export const getActiveDragPlanet = (chartId: string): string | null => {
  const state = getActiveMotion(chartId);
  if (state?.type === 'planet-drag') {
    return state.planet ?? null;
  }
  return null;
};
